import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { Alert, Box, Button, Chip, Divider, Link, Stack, TextField, Typography } from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UtilService from "../../services/UtilService";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import { ADMIN_CRM_FEEDBACK_PATH } from "../crm/crmRoutes";
import AdminFeedbackService from "./AdminFeedbackService";

/** Maps API puzzle state to admin board path segment (see App.js routes). */
const BOARD_STATE_SEGMENTS = {
    DRAFT: "draft",
    READY_TO_REVIEW: "ready-to-review",
    REVIEWED: "reviewed",
    PUBLISHED: "published"
};

/**
 * Path to admin board workflow page for this puzzle ref (opens in new tab).
 * Board routes expect the published puzzle **template** id when present; fallback to puzzle ref id.
 * Crossword → /admin/puzzle/cross_board/... ; grid → /admin/puzzle/grid_board/...
 */
function buildAdminBoardHref(refObj) {
    const boardId = refObj?.template?.id ?? refObj?.id;
    if (!boardId || !refObj?.state) return null;
    const segment = BOARD_STATE_SEGMENTS[refObj.state];
    if (!segment) return null;
    const isCross = refObj.type === "CROSSWORD";
    const base = isCross ? "/admin/puzzle/cross_board" : "/admin/puzzle/grid_board";
    const path = `${base}/${segment}/${boardId}`;
    const search = refObj.state === "PUBLISHED" ? "?fromStep=3" : "";
    return path + search;
}

function PuzzleRefSection({ title, refObj }) {
    if (!refObj) return null;
    const boardHref = buildAdminBoardHref(refObj);
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {title}
            </Typography>
            <Box sx={{ pl: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body2">
                    <strong>Title:</strong> {refObj.title || "—"}
                </Typography>
                <Typography variant="body2">
                    <strong>State:</strong> {refObj.state || "—"} · <strong>Type:</strong> {refObj.type || "—"}
                </Typography>
                <Typography variant="body2">
                    <strong>Difficulty:</strong> {refObj.difficultyLevel || "—"} · <strong>Free:</strong>{" "}
                    {refObj.free != null ? String(refObj.free) : "—"}
                </Typography>
                {refObj.genre?.name && (
                    <Typography variant="body2">
                        <strong>Genre:</strong> {refObj.genre.name}
                    </Typography>
                )}
                {boardHref && (
                    <Box sx={{ mt: 0.5 }}>
                        <Link href={boardHref} target="_blank" rel="noopener noreferrer" variant="body2" fontWeight={600}>
                            Open puzzle (new window)
                        </Link>
                    </Box>
                )}
                {refObj.previewImageUrl && (
                    <Box sx={{ mt: 1 }}>
                        <Link href={refObj.previewImageUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                            Preview image
                        </Link>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default function AdminFeedbackDetail() {
    const { feedbackId } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "error", message: "" });
    const [replyExpanded, setReplyExpanded] = useState(false);
    const [replyMessage, setReplyMessage] = useState("");
    const [replySending, setReplySending] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!feedbackId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        AdminFeedbackService.findById(feedbackId).then((result) => {
            if (cancelled) return;
            setLoading(false);
            if (result.success && result.data) {
                setDetail(result.data);
            } else {
                setDetail(null);
                setSnackbar({ open: true, severity: "error", message: result.error || "Feedback not found" });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [feedbackId]);

    useEffect(() => {
        setReplyExpanded(false);
        setReplyMessage("");
    }, [feedbackId]);

    const handleSendReply = useCallback(async () => {
        const trimmed = replyMessage.trim();
        if (!trimmed) {
            setSnackbar({ open: true, severity: "warning", message: "Enter a message to send." });
            return;
        }
        if (!feedbackId) return;
        setReplySending(true);
        try {
            const result = await AdminFeedbackService.replyByEmail(feedbackId, trimmed);
            if (result.success) {
                setSnackbar({ open: true, severity: "success", message: "Reply sent." });
                setReplyMessage("");
                setReplyExpanded(false);
                const refresh = await AdminFeedbackService.findById(feedbackId);
                if (refresh.success && refresh.data) {
                    setDetail(refresh.data);
                }
            } else {
                setSnackbar({ open: true, severity: "error", message: result.error || "Could not send reply." });
            }
        } finally {
            setReplySending(false);
        }
    }, [replyMessage, feedbackId]);

    const handleDelete = useCallback(async () => {
        if (!feedbackId) return;
        setDeleting(true);
        try {
            const result = await AdminFeedbackService.delete(feedbackId);
            if (result.success) {
                navigate(ADMIN_CRM_FEEDBACK_PATH);
            } else {
                setSnackbar({ open: true, severity: "error", message: result.error || "Could not delete feedback." });
            }
        } finally {
            setDeleting(false);
        }
    }, [feedbackId, navigate]);

    return (
        <Box sx={{ flexGrow: 1, p: 2, maxWidth: { xs: "100%", md: 1200 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ADMIN_CRM_FEEDBACK_PATH)}>
                    Back to list
                </Button>
                {detail && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteOpen(true)}
                        disabled={deleting}
                    >
                        Delete
                    </Button>
                )}
            </Box>

            <Typography variant="h5" gutterBottom>
                Feedback detail
            </Typography>

            {loading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : !detail ? (
                <Typography color="text.secondary">No data.</Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                            gap: { xs: 2, md: 3 },
                            alignItems: "start"
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                                <Chip size="small" label={`ID: ${detail.id}`} variant="outlined" />
                                {detail.stars != null && <Chip size="small" color="primary" label={`${detail.stars} stars`} />}
                                {detail.difficultyDelta != null && (
                                    <Chip size="small" label={`Difficulty Δ: ${detail.difficultyDelta}`} variant="outlined" />
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Created:</strong>{" "}
                                {detail.createdAt != null ? moment(detail.createdAt).format("YYYY-MM-DD HH:mm:ss") : "—"}
                            </Typography>
                            {detail.cloudUser ? (
                                <Box sx={{ pl: 0.5 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        User
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                        <strong>Email:</strong> {detail.cloudUser.email || "—"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                        <strong>ID:</strong> {detail.cloudUser.id || "—"}
                                    </Typography>
                                    {(detail.cloudUser.firstName ||
                                        detail.cloudUser.lastName ||
                                        detail.cloudUser.details?.firstName ||
                                        detail.cloudUser.details?.lastName) && (
                                        <Typography variant="body2">
                                            <strong>Name:</strong>{" "}
                                            {[detail.cloudUser.firstName || detail.cloudUser.details?.firstName, detail.cloudUser.lastName || detail.cloudUser.details?.lastName]
                                                .filter(Boolean)
                                                .join(" ") || "—"}
                                        </Typography>
                                    )}
                                    {detail.cloudUser.userRole != null && detail.cloudUser.userRole !== "" && (
                                        <Typography variant="body2">
                                            <strong>Role:</strong> {detail.cloudUser.userRole}
                                        </Typography>
                                    )}
                                    {UtilService.resolveWebPayPlan(detail.cloudUser) !== "FREE" && (
                                        <Typography variant="body2">
                                            <strong>Plan:</strong> {UtilService.resolveWebPayPlan(detail.cloudUser)}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    <strong>User:</strong> No linked account
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                p: { xs: 0, md: 1.5 },
                                borderRadius: 1,
                                border: { md: 1 },
                                borderColor: { md: "divider" },
                                bgcolor: { md: "grey.50" }
                            }}
                        >
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Puzzle
                            </Typography>
                            <PuzzleRefSection title="Grid puzzle" refObj={detail.grid} />
                            <PuzzleRefSection title="Crossword puzzle" refObj={detail.cross} />
                            {!detail.grid && !detail.cross && (
                                <Typography variant="body2" color="text.secondary">
                                    No linked puzzle.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Divider />
                    <Typography variant="subtitle2" gutterBottom>
                        Comment
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", bgcolor: "grey.50", p: 1.5, borderRadius: 1, border: 1, borderColor: "divider" }}
                    >
                        {detail.comment || "—"}
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                        Replies
                    </Typography>
                    {Array.isArray(detail.replies) && detail.replies.length > 0 ? (
                        <Stack spacing={1.5}>
                            {[...detail.replies]
                                .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
                                .map((r) => (
                                    <Box
                                        key={r.id}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: 1,
                                            borderColor: "divider",
                                            bgcolor: "action.hover"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                alignItems: "baseline",
                                                mb: 0.5
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {r.createdAt != null
                                                    ? moment(r.createdAt).format("YYYY-MM-DD HH:mm:ss")
                                                    : "—"}
                                            </Typography>
                                            {r.id != null && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                                    {r.id}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                            {r.message ?? "—"}
                                        </Typography>
                                    </Box>
                                ))}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No replies yet.
                        </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />
                    <Box>
                        {!replyExpanded ? (
                            <Button variant="contained" color="primary" onClick={() => setReplyExpanded(true)}>
                                Reply
                            </Button>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: "100%" }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Reply by email
                                </Typography>
                                <TextField
                                    label="Message"
                                    placeholder="Reply body only—no greeting or sign-off"
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    multiline
                                    minRows={5}
                                    maxRows={16}
                                    fullWidth
                                    disabled={replySending}
                                />
                                <Alert severity="info" sx={{ alignItems: "flex-start" }}>
                                    The email already opens with a hello to the user and ends with the Puzzle Interact Team
                                    sign-off. Type only the reply that appears in the main message block.
                                </Alert>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    <Button variant="contained" onClick={handleSendReply} disabled={replySending}>
                                        {replySending ? "Sending…" : "Send reply"}
                                    </Button>
                                    <Button
                                        disabled={replySending}
                                        onClick={() => {
                                            setReplyExpanded(false);
                                            setReplyMessage("");
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            <ConfirmDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                title="Delete feedback?"
                targetName={feedbackId}
                message="This will permanently remove this feedback and its replies."
                callback={handleDelete}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
